import React from "react";
import { useLocation } from "react-router-dom";
import { useEditMode } from "../EditModeContext";
import { useLang } from "../LanguageContext";
import { useSettings } from "../SettingsContext";

/**
 * Render admin-editable text. When editMode is ON and the user is admin,
 * hovering shows a dashed outline and clicking opens the shared editor.
 *
 * Props:
 *   - kEn / kFr: the settings keys to store the EN / FR values
 *   - label:     human label for the editor modal
 *   - fallback:  { en, fr } default text if setting is empty
 *   - as:        HTML tag or a render function (children) => JSX
 *   - className: passed through to the underlying element (only when `as` is a string)
 */
export default function EditableText({
  kEn,
  kFr,
  label = "Edit text",
  fallback = { en: "", fr: "" },
  as = "span",
  className = "",
  children,
  ...rest
}) {
  const { editMode, setTarget, isAdmin } = useEditMode();
  const { lang } = useLang();
  const { settings } = useSettings();
  const { pathname } = useLocation();

  const enVal = (settings[kEn] || "").trim() || fallback.en;
  const frVal = (settings[kFr] || "").trim() || fallback.fr;
  const value = lang === "fr" ? frVal : enVal;

  const active = editMode && isAdmin && !pathname.startsWith("/admin");
  const cls = active
    ? `${className} outline outline-1 outline-dashed outline-primary/50 outline-offset-2 cursor-pointer hover:bg-primary/5 hover:outline-primary transition-colors`
    : className;

  const onClick = active
    ? (e) => {
        e.preventDefault();
        e.stopPropagation();
        setTarget({
          kEn,
          kFr,
          label,
          currentEn: (settings[kEn] || "").trim() || fallback.en,
          currentFr: (settings[kFr] || "").trim() || fallback.fr,
        });
      }
    : undefined;

  // Render as function → give raw string + wrapping utilities
  if (typeof children === "function") {
    return children(value, { className: cls, onClick, "data-testid": `editable-text-${kEn}`, ...rest });
  }

  // Render as HTML tag
  const Tag = as;
  return (
    <Tag className={cls} onClick={onClick} data-testid={`editable-text-${kEn}`} {...rest}>
      {value}
    </Tag>
  );
}
